import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ActiveAlerts,
  Chart,
  DataAlert,
  DataAlertByReport,
  NewActiveAlerts,
  Report,
  TriggeredAlertWithActiveAlert,
} from "../../interfaces/interfaces";
import {
  fetchAsyncActiveAlerts,
  fetchAsyncCharts,
  fetchAsyncDataAlertSummary,
  fetchAsyncTriggerAlertHistory,
  fetchAsyncTriggeredAlerts,
} from "./dataAlerts.util";

export interface DataAlertState {
  activeAlertCount: number;
  triggeredAlertCount: number;
  lastMonthAlerts: TriggeredAlertWithActiveAlert[];
  mostTriggeredAlerts: TriggeredAlertWithActiveAlert[];
  mostWorkspaceAlerts: DataAlertByReport[];
  loading: boolean;
  error: any;
  activeAlertSkipCount: number;
  activeAlerts: DataAlert[];
  activeAlertLoading: boolean;
  activeAlertInitialLoaded: boolean;
  triggeredAlertHistory: {
    [x: string]: NewActiveAlerts[];
  };
  dataPiece: {
    [x: string]: Chart;
  };
  chartLoading: boolean;
  triggeredAlertsLoading: boolean;
  triggeredAlerts: NewActiveAlerts[];
  setActiveAlert: {
    alertId: number;
    triggeredAlertId: number;
  };
}

const INITIAL_STATE = {
  activeAlertCount: 0,
  triggeredAlertCount: 0,
  lastMonthAlerts: [],
  mostTriggeredAlerts: [],
  mostWorkspaceAlerts: [],
  loading: false,
  error: null,
  activeAlerts: null,
  activeAlertSkipCount: 0,
  activeAlertLoading: false,
  activeAlertInitialLoaded: false,
  triggeredAlertHistory: {},
  chartLoading: false,
  dataPiece: {},
  triggeredAlertsLoading: false,
  triggeredAlerts: null,
  setActiveAlert: {
    alertId: null,
    triggeredAlertId: null,
  },
} as DataAlertState;

export type AddSummaryPayload = {
  activeAlertCount: number;
  triggeredAlertCount: number;
  lastMonthAlerts: TriggeredAlertWithActiveAlert[];
  mostTriggeredAlerts: TriggeredAlertWithActiveAlert[];
  mostWorkspaceAlerts: DataAlertByReport[];
};

export type SetActiveAlertPayload = {
  alertId: number;
  triggeredAlertId: number;
};

type UpdateActiveAlertPayload = {
  alertIndex: number;
  activeAlert: DataAlert;
};

const DataAlertSlice = createSlice({
  name: "dataAlert",
  initialState: INITIAL_STATE,
  reducers: {
    addSummaryData: (state, action: PayloadAction<AddSummaryPayload>) => {
      state.activeAlertCount = action.payload.activeAlertCount;
      state.triggeredAlertCount = action.payload.triggeredAlertCount;
      state.lastMonthAlerts = action.payload.lastMonthAlerts;
      state.mostTriggeredAlerts = action.payload.mostTriggeredAlerts;
      state.mostWorkspaceAlerts = action.payload.mostWorkspaceAlerts;
    },
    changeActiveAlert: (
      state,
      action: PayloadAction<SetActiveAlertPayload>
    ) => {
      state.setActiveAlert = {
        alertId: action.payload.alertId,
        triggeredAlertId: action.payload.triggeredAlertId,
      };
    },
    updateActiveAlert: (
      state,
      action: PayloadAction<UpdateActiveAlertPayload>
    ) => {
      state.activeAlerts[action.payload.alertIndex] =
        action.payload.activeAlert;
    },
    deleteActiveAlert: (state, action: PayloadAction<number>) => {
      const existingActiveAlerts = [...state.activeAlerts];
      existingActiveAlerts.splice(action.payload, 1);
      state.activeAlerts = existingActiveAlerts;
    },
    deleteTriggeredAlert: (state, action: PayloadAction<number>) => {
      const existingTriggeredAlert = [...state.triggeredAlerts];
      // console.log("before delete", existingTriggeredAlert, action.payload);
      existingTriggeredAlert.splice(action.payload, 1);
      // console.log("after delete", existingTriggeredAlert);
      state.triggeredAlerts = existingTriggeredAlert;
    },
    resetDataAlert: () => INITIAL_STATE,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAsyncDataAlertSummary.pending, (state, action) => {
      console.log(action.meta.arg);
      state.error = null;
      if (action.meta.arg !== "false") {
        state.loading = true;
      }
    });
    builder.addCase(fetchAsyncDataAlertSummary.fulfilled, (state, action) => {
      state.error = null;
      state.activeAlertCount = action.payload.activeAlertCount;
      state.triggeredAlertCount = action.payload.triggeredAlertCount;
      state.lastMonthAlerts = action.payload.lastMonthAlerts;
      state.mostTriggeredAlerts = action.payload.mostTriggeredAlerts;
      state.mostWorkspaceAlerts = action.payload.mostWorkspaceAlerts;
      state.loading = false;
    });
    builder.addCase(fetchAsyncDataAlertSummary.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(fetchAsyncActiveAlerts.pending, (state, action) => {
      state.error = null;
      state.activeAlertLoading = true;
    });
    builder.addCase(fetchAsyncActiveAlerts.fulfilled, (state, action) => {
      state.activeAlerts = action.payload.activeAlerts;
      state.activeAlertLoading = false;
    });
    builder.addCase(fetchAsyncActiveAlerts.rejected, (state, action) => {
      state.activeAlertLoading = false;
      state.error = action.error.message;
      state.activeAlerts = null;
    });
    builder.addCase(
      fetchAsyncTriggerAlertHistory.fulfilled,
      (state, action) => {
        const alertId = action.payload.alertId;
        const triggeredAlertHistory = action.payload.triggeredAlertHistory;
        state.triggeredAlertHistory = {
          ...state.triggeredAlertHistory,
          [`${alertId}`]: triggeredAlertHistory,
        };
      }
    );
    builder.addCase(fetchAsyncCharts.pending, (state, action) => {
      state.error = null;
      state.chartLoading = true;
    });
    builder.addCase(fetchAsyncCharts.fulfilled, (state, action) => {
      const dataPieceObj = action.payload.charts.reduce((prev, currVal) => {
        prev[`${currVal.chartIdentifier}_${currVal.reportVersionId}`] = currVal;
        return prev;
      }, {});
      state.dataPiece = {
        ...state.dataPiece,
        ...dataPieceObj,
      };
      state.chartLoading = false;
    });
    builder.addCase(fetchAsyncCharts.rejected, (state, action) => {
      state.error = action.error.message;
      state.chartLoading = false;
    });

    builder.addCase(fetchAsyncTriggeredAlerts.pending, (state, action) => {
      state.error = null;
      state.triggeredAlertsLoading = true;
    });
    builder.addCase(fetchAsyncTriggeredAlerts.fulfilled, (state, action) => {
      state.triggeredAlerts = action.payload.triggeredAlerts;
      state.triggeredAlertsLoading = false;
    });
    builder.addCase(fetchAsyncTriggeredAlerts.rejected, (state, action) => {
      state.triggeredAlertsLoading = false;
      state.error = action.error.message;
      state.triggeredAlerts = null;
    });
  },
});

export const {
  addSummaryData,
  resetDataAlert,
  changeActiveAlert,
  updateActiveAlert,
  deleteActiveAlert,
  deleteTriggeredAlert,
} = DataAlertSlice.actions;

export const DataAlertReducer = DataAlertSlice.reducer;
